import React, { useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Loader, ScrollArea, Code,
  SimpleGrid, TextInput, Select, SegmentedControl, Pagination, Drawer, Tabs, Anchor, Button,
} from '@mantine/core';
import { IconSchool } from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import TeachModal from '../teaching/TeachModal';
import config from '../../config';
import DraftBadge from '../common/DraftBadge';

// Which teaching surface a logged call maps to (only Vesper agent calls are teachable).
const teachSurface = (src) => (src === 'vesper_headline' ? 'headline' : src === 'vesper_judgement' ? 'judgement' : null);

const PAGE_SIZE = 50;
const SOURCE_COLOR = {
  funnel_parse: 'teal', vesper_summarize: 'grape', vesper_judgement: 'violet',
};
const money = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—');
function pretty(v) {
  if (v == null || v === '') return '(none)';
  try { return JSON.stringify(typeof v === 'string' ? JSON.parse(v) : v, null, 2); } catch { return String(v); }
}

const SORTS = {
  'Newest':       ['RequestTime', 'desc'],
  'Highest cost': ['est_cost_usd', 'desc'],
  'Most tokens':  ['TotalTokens', 'desc'],
  'Slowest':      ['LatencyMs', 'desc'],
};

export default function AiTraffic() {
  const [source, setSource] = useState('');
  const [model, setModel]   = useState('');
  const [errOnly, setErrOnly] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('Newest');
  const [page, setPage] = useState(1);
  const [selId, setSelId] = useState(null);
  const [teach, setTeach] = useState(null);   // { surface, context, idealOutput }

  const summaryUrl = `${config.apiBaseUrl}/api/ai/traffic/summary`;
  const { data: summary } = useAuthenticatedData(summaryUrl);

  const listUrl = useMemo(() => {
    const [sf, sd] = SORTS[sort];
    const p = new URLSearchParams({ page: page - 1, pageSize: PAGE_SIZE, sortField: sf, sortDir: sd });
    if (source) p.set('source', source);
    if (model) p.set('model', model);
    if (errOnly === 'errors') p.set('hasError', 'true');
    if (q.trim()) p.set('q', q.trim());
    return `${config.apiBaseUrl}/api/ai/traffic?${p.toString()}`;
  }, [source, model, errOnly, q, sort, page]);
  const { data, isLoading } = useAuthenticatedData(listUrl);

  const detailUrl = selId ? `${config.apiBaseUrl}/api/ai/traffic/${selId}` : null;
  const { data: detail } = useAuthenticatedData(detailUrl);

  const { data: pricing } = useAuthenticatedData(`${config.apiBaseUrl}/api/ai/pricing`);

  const groups = summary?.groups || [];
  const totals = summary?.totals || {};
  const sourceOpts = ['', ...Array.from(new Set(groups.map((g) => g.Source).filter(Boolean)))];
  const modelOpts  = ['', ...Array.from(new Set(groups.map((g) => g.ModelName).filter(Boolean)))];
  const rows = data?.rows || [];
  const pageCount = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <div>
        <Title order={2}>AI Traffic</Title>
        <Text size="sm" c="dimmed">Every model call across the funnel and sales enrichment pipelines — tokens, latency, and list-price cost.</Text>
      </div>

      {/* Summary cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <StatCard label="Calls" value={num(totals.calls)} />
        <StatCard label="Input tokens" value={num(totals.input_tokens)} />
        <StatCard label="Output tokens" value={num(totals.output_tokens)} />
        <StatCard label="Est. cost (list)" value={money(totals.est_cost_usd)} accent="teal" />
      </SimpleGrid>

      <Tabs defaultValue="traffic">
        <Tabs.List>
          <Tabs.Tab value="traffic">Traffic</Tabs.Tab>
          <Tabs.Tab value="by-source">By source</Tabs.Tab>
          <Tabs.Tab value="pricing">Price book</Tabs.Tab>
        </Tabs.List>

        {/* ── Traffic list ── */}
        <Tabs.Panel value="traffic" pt="sm">
          <Group gap="xs" mb="sm" wrap="wrap">
            <Select size="xs" placeholder="Source" data={sourceOpts.map((s) => ({ value: s, label: s || 'All sources' }))} value={source} onChange={(v) => { setSource(v || ''); setPage(1); }} w={170} />
            <Select size="xs" placeholder="Model" data={modelOpts.map((s) => ({ value: s, label: s || 'All models' }))} value={model} onChange={(v) => { setModel(v || ''); setPage(1); }} w={160} />
            <SegmentedControl size="xs" value={errOnly} onChange={(v) => { setErrOnly(v); setPage(1); }} data={[{ label: 'All', value: 'all' }, { label: 'Errors', value: 'errors' }]} />
            <TextInput size="xs" placeholder="Search correlation / office…" value={q} onChange={(e) => { setQ(e.currentTarget.value); setPage(1); }} w={230} />
            <Select size="xs" data={Object.keys(SORTS)} value={sort} onChange={(v) => setSort(v || 'Newest')} w={140} />
          </Group>

          <Paper withBorder radius="md">
            <ScrollArea>
              <Table fz="xs" striped highlightOnHover miw={980}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Time</Table.Th><Table.Th>Source</Table.Th><Table.Th>Model</Table.Th>
                    <Table.Th>Prompt</Table.Th><Table.Th ta="right">In</Table.Th><Table.Th ta="right">Out</Table.Th>
                    <Table.Th ta="right">ms</Table.Th><Table.Th ta="right">Cost</Table.Th>
                    <Table.Th>Finish</Table.Th><Table.Th>Correlation</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isLoading && <Table.Tr><Table.Td colSpan={10}><Group justify="center" p="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>}
                  {!isLoading && rows.map((r) => (
                    <Table.Tr key={r.Id} style={{ cursor: 'pointer' }} onClick={() => setSelId(r.Id)}>
                      <Table.Td>{fmt(r.RequestTime)}</Table.Td>
                      <Table.Td><Badge size="xs" variant="light" color={SOURCE_COLOR[r.Source] || 'gray'}>{r.Source || '—'}</Badge></Table.Td>
                      <Table.Td>{r.ModelName}</Table.Td>
                      <Table.Td>{r.PromptVersion || '—'}</Table.Td>
                      <Table.Td ta="right">{num(r.InputTokens)}</Table.Td>
                      <Table.Td ta="right">{num(r.OutputTokens)}</Table.Td>
                      <Table.Td ta="right">{r.LatencyMs ?? '—'}</Table.Td>
                      <Table.Td ta="right">{money(r.est_cost_usd)}</Table.Td>
                      <Table.Td>{r.HasError ? <Badge size="xs" color="red">error</Badge> : (r.FinishReason || '—')}</Table.Td>
                      <Table.Td><Text size="xs" truncate maw={180}>{r.CorrelationId || '—'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                  {!isLoading && rows.length === 0 && <Table.Tr><Table.Td colSpan={10}><Text c="dimmed" ta="center" py="md">No traffic.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
          <Group justify="space-between" mt="xs">
            <Text size="xs" c="dimmed">{num(data?.total)} calls · {money(data?.totalCost)} (filtered)</Text>
            <Pagination size="sm" total={pageCount} value={page} onChange={setPage} />
          </Group>
        </Tabs.Panel>

        {/* ── By source rollup ── */}
        <Tabs.Panel value="by-source" pt="sm">
          <Paper withBorder radius="md">
            <Table fz="xs" striped>
              <Table.Thead><Table.Tr>
                <Table.Th>Source</Table.Th><Table.Th>Model</Table.Th><Table.Th ta="right">Calls</Table.Th>
                <Table.Th ta="right">Input tok</Table.Th><Table.Th ta="right">Output tok</Table.Th>
                <Table.Th ta="right">Avg ms</Table.Th><Table.Th ta="right">Errors</Table.Th><Table.Th ta="right">Est. cost</Table.Th>
              </Table.Tr></Table.Thead>
              <Table.Tbody>
                {groups.map((g, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Badge size="xs" variant="light" color={SOURCE_COLOR[g.Source] || 'gray'}>{g.Source}</Badge></Table.Td>
                    <Table.Td>{g.ModelName}</Table.Td>
                    <Table.Td ta="right">{num(g.calls)}</Table.Td>
                    <Table.Td ta="right">{num(g.input_tokens)}</Table.Td>
                    <Table.Td ta="right">{num(g.output_tokens)}</Table.Td>
                    <Table.Td ta="right">{g.avg_latency_ms != null ? Math.round(g.avg_latency_ms) : '—'}</Table.Td>
                    <Table.Td ta="right">{g.errors ? <Text c="red">{g.errors}</Text> : 0}</Table.Td>
                    <Table.Td ta="right">{money(g.est_cost_usd)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        {/* ── Price book ── */}
        <Tabs.Panel value="pricing" pt="sm">
          <Text size="xs" c="dimmed" mb="xs">USD per 1M tokens · short-context, standard billing. Est. cost above uses the input+output list rate (no cached-input discount applied).</Text>
          <Paper withBorder radius="md">
            <Table fz="xs" striped>
              <Table.Thead><Table.Tr>
                <Table.Th>Model</Table.Th><Table.Th>Tier</Table.Th><Table.Th ta="right">Input</Table.Th>
                <Table.Th ta="right">Cached in</Table.Th><Table.Th ta="right">Cache write</Table.Th><Table.Th ta="right">Output</Table.Th><Table.Th>Notes</Table.Th>
              </Table.Tr></Table.Thead>
              <Table.Tbody>
                {(pricing || []).map((p, i) => (
                  <Table.Tr key={i}>
                    <Table.Td fw={600}>{p.model_name}</Table.Td>
                    <Table.Td>{p.context_tier}</Table.Td>
                    <Table.Td ta="right">${p.input_per_1m}</Table.Td>
                    <Table.Td ta="right">{p.cached_input_per_1m != null ? `$${p.cached_input_per_1m}` : '—'}</Table.Td>
                    <Table.Td ta="right">{p.cache_write_per_1m != null ? `$${p.cache_write_per_1m}` : '—'}</Table.Td>
                    <Table.Td ta="right">${p.output_per_1m}</Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{p.notes}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Detail drawer */}
      <Drawer opened={!!selId} onClose={() => setSelId(null)} position="right" size="xl"
        title={detail ? `Call #${detail.Id} · ${detail.Source} · ${detail.ModelName}` : 'Loading…'}>
        {detail && (
          <Stack gap="sm">
            <SimpleGrid cols={2}>
              <Meta label="Time" value={fmt(detail.RequestTime)} />
              <Meta label="Prompt version" value={detail.PromptVersion || '—'} />
              <Meta label="Tokens (in / out)" value={`${num(detail.InputTokens)} / ${num(detail.OutputTokens)}`} />
              <Meta label="Latency" value={detail.LatencyMs != null ? `${detail.LatencyMs} ms` : '—'} />
              <Meta label="Est. cost" value={money(detail.est_cost_usd)} />
              <Meta label="Finish" value={detail.HasError ? 'error' : (detail.FinishReason || '—')} />
              <Meta label="Correlation" value={detail.CorrelationId || '—'} />
              <Meta label="Office / msg" value={detail.OfficeId || '—'} />
            </SimpleGrid>
            {detail.ErrorMessage && <Text c="red" size="sm">Error: {detail.ErrorMessage}</Text>}
            {teachSurface(detail.Source) && (
              <Button size="xs" variant="light" color="grape" leftSection={<IconSchool size={14} />}
                onClick={() => setTeach({ surface: teachSurface(detail.Source), context: pretty(detail.Request), idealOutput: pretty(detail.Response) })}>
                Teach from this call
              </Button>
            )}
            <Tabs defaultValue="response">
              <Tabs.List>
                <Tabs.Tab value="response">Response</Tabs.Tab>
                <Tabs.Tab value="request">Request</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="response" pt="xs">
                <ScrollArea.Autosize mah={520}><Code block style={{ fontSize: 12 }}>{pretty(detail.Response)}</Code></ScrollArea.Autosize>
              </Tabs.Panel>
              <Tabs.Panel value="request" pt="xs">
                <ScrollArea.Autosize mah={520}><Code block style={{ fontSize: 12 }}>{pretty(detail.Request)}</Code></ScrollArea.Autosize>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Drawer>

      <TeachModal
        opened={!!teach}
        onClose={() => setTeach(null)}
        surface={teach?.surface}
        context={teach?.context || ''}
        idealOutput={teach?.idealOutput || ''}
        onSaved={() => setTeach(null)}
      />
    </Stack>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700} size="xl" c={accent}>{value}</Text>
    </Paper>
  );
}
function Meta({ label, value }) {
  return (<div><Text size="xs" c="dimmed">{label}</Text><Text size="sm">{value}</Text></div>);
}
